import React, { useContext, useState, useEffect, useRef } from 'react'
import './Aivoice.css';
import Box from '@mui/material/Box';
import MicNoneIcon from '@mui/icons-material/MicNone';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import MicIcon from '@mui/icons-material/Mic';
import { dataContext } from '../../components/context/userContect';
import Listeningaigif from './images/Black_color.jpg';
import run from '../../components/mainapi'; // Import the API directly

function Aivoice() {
    const { speaking, setSpeeking, responseAi, setResponseAi, setAiSpeak } = useContext(dataContext);
    
    const [isPaused, setIsPaused] = useState(false);
    const [storedResponse, setStoredResponse] = useState('');
    const [isReady, setIsReady] = useState(true);
    const [language, setLanguage] = useState('en-US'); // Default language
    const [micPermissionState, setMicPermissionState] = useState('pending'); // 'pending', 'granted', 'denied'
    const audioRef = useRef(null);
    const recognitionRef = useRef(null);
    
    // Function to directly handle "Who created you?" question
    const handleDirectQuestion = (question) => {
        const lowercaseQ = question.toLowerCase().trim();
        
        if (lowercaseQ.includes("who created you") || 
            lowercaseQ.includes("who made you") || 
            lowercaseQ === "who are you" || 
            lowercaseQ.includes("who developed you") ||
            lowercaseQ.includes("who designed you") ||
            lowercaseQ.includes("your creator") ||
            lowercaseQ.includes("who is your maker") ||
            lowercaseQ.includes("who built you")) {
            return "I was created by ChatBox AI.";
        }
        return null; // No direct answer
    };
    
    // Function to handle speech synthesis with a word limit
    const processAndSpeakResponse = async (prompt) => {
        try {
            // Check for direct questions first
            const directAnswer = handleDirectQuestion(prompt);
            
            let response;
            if (directAnswer) {
                response = directAnswer;
            } else {
                // Get response from the API
                response = await run(prompt);
                
                // Clean up the response for speech
                response = response
                    .replace(/<[^>]*>/g, '') // Remove HTML tags
                    .replace(/\*\*/g, '')    // Remove markdown
                    .replace(/\*/g, '')
                    .replace(/\n/g, ' ')    // Replace newlines with spaces
                    .replace(/\s+/g, ' ')   // Replace multiple spaces with single space
                    .trim();
                
                // Limit response length if needed
                const words = response.split(/\s+/);
                if (words.length > 50) {
                    response = words.slice(0, 50).join(' ') + '...';
                }
            }
            
            // Store and speak the response
            setStoredResponse(response);
            setAiSpeak(response);
            speakText(response);
            setResponseAi(true);
            
            // Set timeout to reset state after speech
            const estimatedDuration = Math.max(5000, response.length * 90); // Increased duration
            setTimeout(() => {
                setSpeeking(false);
                setIsReady(true);
                if (recognitionRef.current) {
                    try {
                        recognitionRef.current.stop();
                    } catch (e) {
                        console.log("Recognition already stopped");
                    }
                }
            }, estimatedDuration);
            
        } catch (error) {
            console.error("Error processing speech:", error);
            const errorMsg = "I'm sorry, but I encountered an error. Please try again.";
            setStoredResponse(errorMsg);
            setAiSpeak(errorMsg);
            speakText(errorMsg);
            
            setTimeout(() => {
                setSpeeking(false);
                setIsReady(true);
                if (recognitionRef.current) {
                    try {
                        recognitionRef.current.stop();
                    } catch (e) {
                        console.log("Recognition already stopped");
                    }
                }
            }, 3000);
        }
    };
    
    // Toggle between Hindi and English
    const toggleLanguage = () => {
        setLanguage(prev => prev === 'en-US' ? 'hi-IN' : 'en-US');
    };
    
    // Improved speech function with better mobile support
    const speakText = (text) => {
        const synth = window.speechSynthesis;
        
        // Cancel any ongoing speech
        synth.cancel();
        
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.volume = 1;
        utterance.rate = 1;
        utterance.pitch = 1;
        utterance.lang = language;
        
        // Get available voices and set the appropriate one
        let voices = synth.getVoices();
        if (voices.length === 0) {
            // If voices aren't loaded yet, wait for them
            speechSynthesis.onvoiceschanged = () => {
                voices = synth.getVoices();
                setVoiceForLanguage(voices);
            };
        } else {
            setVoiceForLanguage(voices);
        }
        
        function setVoiceForLanguage(availableVoices) {
            // Try to find a voice that matches the selected language
            const languageCode = language.split('-')[0];
            const voiceForLanguage = availableVoices.find(voice => 
                voice.lang.includes(languageCode) && 
                (voice.name.includes('Google') || voice.name.includes('Natural'))
            ) || availableVoices.find(voice => 
                voice.lang.includes(languageCode)
            );
            
            if (voiceForLanguage) {
                utterance.voice = voiceForLanguage;
            }
        }
        
        // Handle speech events
        utterance.onend = () => {
            setIsPaused(false);
            setIsReady(true);
        };
        
        utterance.onerror = (event) => {
            console.error("Speech synthesis error:", event);
            setIsPaused(false);
            setIsReady(true);
        };
        
        // Support for iOS and other mobile devices
        if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
            // iOS requires user interaction before audio can play
            if (audioRef.current) {
                audioRef.current.play().then(() => {
                    synth.speak(utterance);
                }).catch(e => {
                    console.log("Mobile audio play failed", e);
                    synth.speak(utterance);
                });
            } else {
                synth.speak(utterance);
            }
        } else {
            synth.speak(utterance);
        }
    };
    
    // Function to handle play/pause of speech
    const handleSpeechPlayback = (action) => {
        const synth = window.speechSynthesis;
        
        if (action === 'pause') {
            if (synth.speaking) {
                synth.pause();
                setIsPaused(true);
            }
        } else if (action === 'resume') {
            if (synth.paused) {
                synth.resume();
                setIsPaused(false);
            } else if (storedResponse) {
                // Play stored response
                speakText(storedResponse);
                setIsPaused(false);
            }
        }
    };
    
    // Enhanced microphone permission check
    const checkMicrophonePermission = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            // Successfully got permission
            stream.getTracks().forEach(track => track.stop());
            setMicPermissionState('granted');
            return true;
        } catch (error) {
            console.error("Microphone permission error:", error);
            setMicPermissionState('denied');
            
            // Show appropriate error message based on error type
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                alert("Microphone access was denied. Please enable it in your browser settings and refresh the page.");
            } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
                alert("No microphone found. Please check your microphone connection and refresh the page.");
            } else {
                alert("Error accessing microphone. Please check browser settings and refresh the page.");
            }
            return false;
        }
    };

    // Function to request microphone access
    const requestMicrophoneAccess = async () => {
        setIsReady(false);
        const hasPermission = await checkMicrophonePermission();
        if (hasPermission) {
            setIsReady(true);
        }
        return hasPermission;
    };

    // Start listening when button is clicked with enhanced permission handling
    const startListening = async () => {
        if (!isReady) return;

        // Check/request microphone permission first
        const hasPermission = await requestMicrophoneAccess();
        if (!hasPermission) {
            setSpeeking(false);
            setIsReady(true);
            return;
        }

        setSpeeking(true);
        setResponseAi(false);
        setIsReady(false);
        
        // Create a new SpeechRecognition instance
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            alert("Speech recognition is not supported in this browser. Please try Chrome, Edge, or Safari.");
            setSpeeking(false);
            setIsReady(true);
            return;
        }
        
        try {
            const newRecognition = new SpeechRecognition();
            recognitionRef.current = newRecognition;
            
            // Configure recognition with longer timeouts
            newRecognition.continuous = false;
            newRecognition.interimResults = true; // Enable interim results
            newRecognition.lang = language;
            newRecognition.maxAlternatives = 1;
            
            // Set a longer recognition timeout
            const recognitionTimeout = 10000; // 10 seconds
            let timeoutId;
            let hasRecognizedSpeech = false;
            
            // Handle interim results to detect speech early
            newRecognition.onresult = (event) => {
                hasRecognizedSpeech = true;
                const transcript = event.results[0][0].transcript;
                
                // Only process final results
                if (event.results[0].isFinal) {
                    console.log("Final transcript:", transcript);
                    clearTimeout(timeoutId);
                    setAiSpeak(transcript);
                    processAndSpeakResponse(transcript);
                } else {
                    // Show interim results to give user feedback
                    console.log("Interim transcript:", transcript);
                    setAiSpeak("Listening: " + transcript);
                }
            };
            
            // Handle recognition end
            newRecognition.onend = () => {
                console.log("Speech recognition ended");
                clearTimeout(timeoutId);
                
                // If no speech was detected and we're not processing a response
                if (!hasRecognizedSpeech && !responseAi) {
                    // Auto-retry once if no speech was detected
                    if (!recognitionRef.current.isRetrying) {
                        console.log("No speech detected, retrying...");
                        recognitionRef.current.isRetrying = true;
                        try {
                            newRecognition.start();
                            return;
                        } catch (err) {
                            console.error("Retry failed:", err);
                        }
                    }
                    setSpeeking(false);
                    setIsReady(true);
                    setAiSpeak("No speech detected. Please try again.");
                }
            };
            
            // Handle recognition errors
            newRecognition.onerror = (event) => {
                console.error("Recognition error:", event.error);
                clearTimeout(timeoutId);
                
                let errorMessage = "An error occurred with speech recognition. ";
                switch (event.error) {
                    case 'not-allowed':
                        errorMessage = "Please enable microphone access in your browser settings.";
                        break;
                    case 'no-speech':
                        errorMessage = "No speech was detected. Please try again and speak clearly into your microphone.";
                        break;
                    case 'network':
                        errorMessage = "Please check your internet connection.";
                        break;
                    case 'audio-capture':
                        errorMessage = "No microphone was found. Please ensure your microphone is properly connected.";
                        break;
                    default:
                        errorMessage += "Please try again.";
                }
                
                alert(errorMessage);
                setSpeeking(false);
                setIsReady(true);
            };
            
            // Special handling for mobile devices
            if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
                // Force short timeout for mobile
                newRecognition.continuous = false;
                
                // Extra timeout for mobile mic
                setTimeout(() => {
                    try {
                        newRecognition.start();
                        console.log("Mobile recognition started");
                        
                        // Set timeout for mobile
                        timeoutId = setTimeout(() => {
                            if (!hasRecognizedSpeech && recognitionRef.current) {
                                recognitionRef.current.stop();
                            }
                        }, recognitionTimeout);
                        
                    } catch (err) {
                        console.error("Mobile recognition start error:", err);
                        setSpeeking(false);
                        setIsReady(true);
                    }
                }, 300);
            } else {
                // Desktop start
                try {
                    newRecognition.start();
                    console.log("Desktop recognition started");
                    
                    // Set timeout for desktop
                    timeoutId = setTimeout(() => {
                        if (!hasRecognizedSpeech && recognitionRef.current) {
                            recognitionRef.current.stop();
                        }
                    }, recognitionTimeout);
                    
                } catch (error) {
                    console.error("Recognition start error:", error);
                    setSpeeking(false);
                    setIsReady(true);
                }
            }
        } catch (error) {
            console.error("Error setting up speech recognition:", error);
            alert("Failed to start speech recognition. Please try again.");
            setSpeeking(false);
            setIsReady(true);
        }
    };
    
    // Special handling for iOS devices which have stricter audio policies
    useEffect(() => {
        // Initial microphone permission check
        checkMicrophonePermission();
        
        // Preload voices
        const loadVoices = () => {
            window.speechSynthesis.getVoices();
        };
        
        // Wait for voiceschanged event
        window.speechSynthesis.onvoiceschanged = loadVoices;
        loadVoices();
        
        // Detect device type for special handling
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        
        // Create audio context for iOS/mobile
        let audioContext;
        try {
            audioContext = new (window.AudioContext || window.webkitAudioContext)();
        } catch (e) {
            console.warn('AudioContext not supported');
        }
        
        const initializeAudio = async () => {
            if (audioContext?.state === 'suspended') {
                await audioContext.resume();
            }
            
            // Play silent audio to enable audio system
            const silentPlay = async () => {
                try {
                    const audio = new Audio();
                    // Use a very short silent audio file or oscillator
                    audio.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';
                    await audio.play();
                    audio.pause();
                } catch (e) {
                    console.log("Silent audio initialization failed", e);
                }
            };
            
            if (isMobile) {
                await silentPlay();
            }
        };
        
        // Handle user interaction to initialize audio
        const handleInteraction = async () => {
            await initializeAudio();
            // Re-check microphone permission after user interaction
            await checkMicrophonePermission();
        };
        
        // Add interaction listeners
        document.addEventListener('touchstart', handleInteraction, { once: true });
        document.addEventListener('click', handleInteraction, { once: true });
        
        // Cleanup function
        return () => {
            document.removeEventListener('touchstart', handleInteraction);
            document.removeEventListener('click', handleInteraction);
            if (audioContext) {
                audioContext.close().catch(console.error);
            }
        };
    }, []);
    
    return (
        <>
            <Box className='chatseaction1'>
                {/* Main ai gif start */}
                <Box className='topsecation1'>
                    {/* Main ai gif-1 start */}
                    {!speaking ? (
                        <Box 
                            className={`circle ${isReady ? 'ready-to-listen' : ''}`}
                            onClick={() => isReady && startListening()}
                        >
                            <Box className="wave-container">
                                <Box className="wave"></Box>
                            </Box>
                            {isReady && (
                                <Box className="ready-indicator">
                                    <span>Tap to Speak</span>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Box className='maker_king'>
                            {!responseAi ? (
                                <Box className="gif-container">
                                    <img src={Listeningaigif} alt="" />
                                    <Box className="mic-icon">
                                        <MicNoneIcon />
                                    </Box>
                                </Box>
                            ) : (
                                <Box className='replay_ai'>
                                    <Box className="ai-container">
                                        <Box className="wave1">
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                    {/* Main ai gif-1 End */}
                </Box>
                {/* Main ai gif End */}

                {/* Main ai speak Button start */}
                <Box className='bottomsecation2'>
                    <Box className='bottomsecation1'>
                        <button 
                            className={`speak-button ${isReady ? 'ready' : 'not-ready'}`}
                            onClick={() => isReady && startListening()}
                            disabled={!isReady}
                        >
                            {isReady ? 'Speak' : 'Listening...'}
                            <MicIcon />
                        </button>
                        
                        {/* Language toggle button */}
                        <button 
                            className="language-button"
                            onClick={toggleLanguage}
                        >
                            {language === 'en-US' ? 'हिंदी' : 'English'}
                        </button>
                        
                        {/* Add Play/Pause buttons for Push and Play functionality */}
                        {responseAi && storedResponse && (
                            <Box className="playback-controls">
                                {isPaused ? (
                                    <button 
                                        className="control-button play-button"
                                        onClick={() => handleSpeechPlayback('resume')}
                                    >
                                        <PlayArrowIcon />
                                    </button>
                                ) : (
                                    <button 
                                        className="control-button pause-button"
                                        onClick={() => handleSpeechPlayback('pause')}
                                    >
                                        <PauseIcon />
                                    </button>
                                )}
                            </Box>
                        )}
                    </Box>
                    
                    <p>© 2025 Copyright: ChatBox Ai</p>
                </Box>
                {/* Main ai speak Button End */}
            </Box>
        </>
    );
}

export default Aivoice;
